'use client';

/* eslint-disable react-hooks/exhaustive-deps */
import { Icon } from '@iconify/react';
import React, { useState, useEffect } from 'react';

import Drawer from '@mui/material/Drawer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Paper,
  Switch,
  Button,
  Divider,
  useTheme,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';

import { fCurrency } from 'src/utils/format-number';
import { useAnyLoading } from 'src/utils/useAnythingLoading';
import { USERE_DETAILS, USER_ID_STORAGE_KEY } from 'src/utils/constants';
import {
  notify,
  paymentModes,
  getCardTypeIcon,
  isValidSTCPayNumber,
  visibleViewsInPaymentModal,
} from 'src/utils/helper';

import { useLocale, useTranslate } from 'src/locales';
import { CONFIG } from 'src/global-config';
import { useGetSavedCards } from 'src/api/payment/cards';
import { useGetPADStatus } from 'src/api/Order/useOrder';
import { useAddCardCookiePoller } from 'src/api/payment/useAddCardCookiePoller';
import { useGetWalletBalance, useGetCompanyCreditDetails } from 'src/api/payment/wallet';

import { EmptyContent } from 'src/components/empty-content';

import { decryptData } from '../encrypt';

const PaymentDrawer = ({
  open,
  onClose,
  amount,

  onStcInit,
  onStcVerify,
  onCardPayment,
  onCreditPayment,
  onWalletPayment,
  visibleView,
  setVisibleView,
  paymentModesAvailable = paymentModes,
  isCombinationEnabled = false,
  onPad,
  isWalletAvailable = true,
}) => {
  const theme = useTheme();
  // const { t } = useTranslate();
  const { t } = useLocale();

  // const { user } = useAuthContext();
  const user = decryptData(sessionStorage.getItem(USERE_DETAILS)) || {};
  const isLoading = useAnyLoading('payment_');

  const { data: padData, refetch: refetchGetPADStatus } = useGetPADStatus();
  const { data: companyCreditDetails, refetch: getCompanyCreditDetails } =
    useGetCompanyCreditDetails();
  const { data: savedCards, refetch: refetchCards } = useGetSavedCards();
  const { data: walletData, mutateAsync: getWalletBalance } = useGetWalletBalance();

  const [selectedCard, setSelectedCard] = useState('');
  const [selectedCardDetails, setSelectedCardDetails] = useState(null);
  const [isWallet, setIsWallet] = useState(false);
  const [creditDetails, setCreditDetails] = useState(null);
  const [cvv, setCvv] = useState('');
  const [stcDetails, setStcDetails] = useState({
    number: '',
    otp: '',
  });

  const userID = sessionStorage.getItem(USER_ID_STORAGE_KEY);

  // To Watch if Card Got added or not
  const { startPolling, stopPolling } = useAddCardCookiePoller({
    onSuccess: () => {
      console.log('✅ Card added successfully');
      // refetchCards();
      refetchCards();
      setVisibleView(null);
      stopPolling();
    },
    onError: () => {
      console.log('❌ Failed to add card');
      stopPolling();
    },
    onComplete: () => {
      console.log('✅ Polling complete');
      stopPolling();
    },
  });

  const isRTL = theme.direction === 'rtl';

  useEffect(() => {
    if (open) {
      setSelectedCard('');
      setSelectedCardDetails(null);
      setCvv('');
      setIsWallet(false);
      setCreditDetails(null);
      getWalletBalance();
      refetchCards();
      refetchGetPADStatus();
      getCompanyCreditDetails();
    }
  }, [open]);

  useEffect(() => {
    if (companyCreditDetails) {
      setCreditDetails(companyCreditDetails?.error || companyCreditDetails?.success);
    }
  }, [companyCreditDetails]);

  const handleCardSelect = (card) => {
    setSelectedCard(card.cardNumber);
    setSelectedCardDetails(card);
    setCvv('');

    if (amount <= walletData?.amount) {
      setIsWallet(false);
    }
    if (!isCombinationEnabled) {
      // Below 1 function disables Combination Payment
      setIsWallet(false);
    }
  };

  const handlePaymentModeSelect = (mode) => {
    setSelectedCard(mode);
    setSelectedCardDetails(null);
    setCvv('');

    if (amount <= walletData?.amount) {
      setIsWallet(false);
    }

    if (!isCombinationEnabled) {
      // Below 1 function disables Combination Payment
      setIsWallet(false);
    }
    if (mode === 'add-card') {
      startPolling();
      setVisibleView(mode);
    }
    if (mode === 'pad') {
      setIsWallet(false);
    }
  };

  //  Funtion to make payment modes disabled if they does not exist for user or they are blocked to use it
  const isPaymentModeDisabled = (mode) => {
    if (mode === 'pad') {
      return (
        (padData?.paymentPendingOrders > 0 && padData?.success === 'false') ||
        user?.registeredAs === 'individual'
      );
    }
    if (mode == 'credit') {
      return (
        !!companyCreditDetails?.error?.message ||
        !!companyCreditDetails?.error ||
        creditDetails?.balanceCredit < amount
      );
    }
    return false;
  };

  // Handle Arrow back
  const handleBackArrow = () => {
    if (isLoading) {
      notify('error', 'Cant Close Payment drawer for now');
      return;
    }
    stopPolling();
    setIsWallet(false);
    if (selectedCard) {
      setVisibleView(null);
      setSelectedCard(null);
      setCvv('');
      setStcDetails({
        number: '',
      });
      if (selectedCardDetails) {
        setSelectedCardDetails(null);
        onClose?.();
      }
      // stopCardPaymentPolling();
      return;
    }
    setSelectedCardDetails(null);
    onClose?.();
  };

  // Title of Drawer
  const getTitle = () => {
    switch (visibleView) {
      case 'add-card':
        return t('Add_Card_Details');
      case 'stc':
        return t('Enter_STC_Pay_Number');

      default:
        return t('Select_Payment_mode');
    }
  };

  /**
   * Initialize STC Payment (It will send OTP and need to enter it in next screen)
   * also it will return STC standard response like token, stcPayPmtReference, otpReference
   */
  const initSTCPayment = () => {
    if (isValidSTCPayNumber(stcDetails.number)) {
      onStcInit?.(stcDetails?.number, isWallet, walletData?.amount);
    }
  };

  /**
   * Verify STC Payment after adding OTP in input
   */
  const verifySTCPayment = () => {
    onStcVerify?.(stcDetails, isWallet, walletData?.amount);
  };

  /**
   * Handle Pay Button Click
   */
  const handlePay = () => {
    // If Card selected cvv and card details will be there
    if (cvv && selectedCard && selectedCardDetails) {
      onCardPayment?.(selectedCardDetails, cvv, isWallet, walletData?.amount);
      return;
    }

    // Handle Wallet Payment
    if (!selectedCard && isWallet) {
      if (walletData?.amount >= amount) {
        onWalletPayment?.();
      } else {
        notify('error', t('Insufficient_Balance'));
      }
    }

    if (selectedCard === 'stc') {
      setTimeout(
        () => {
          setVisibleView(selectedCard);
        },
        isWallet ? 1000 : 0
      );
    }

    if (selectedCard === 'pad') {
      onPad?.(isWallet);
    }

    if (selectedCard === 'credit') {
      onCreditPayment?.();
    }
  };

  const toggleWallet = () => {
    if (walletData?.amount === 0) {
      return;
    }

    if (amount <= walletData?.amount) {
      setSelectedCard('');
      setSelectedCardDetails(null);
    }

    if (!isCombinationEnabled) {
      // Below 2 function disables Combination Payment
      setSelectedCard('');
      setSelectedCardDetails(null);
    }

    if (selectedCard === 'pad') {
      setSelectedCard('');
      setSelectedCardDetails(null);
    }

    setIsWallet((prev) => !prev);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleBackArrow}
      ModalProps={{ sx: { zIndex: 1550 } }}
      slotProps={{
        paper: {
          sx: {
            p: 1,
            width: { xs: '100%', md: 450 },
          },
        },
      }}
    >
      <Box sx={{ px: 4, py: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <ArrowBackIcon
            onClick={handleBackArrow}
            sx={{ cursor: 'pointer', transform: isRTL ? 'rotate(180deg)' : 'none' }}
          />
          <Typography variant="h6" textAlign="center">
            {getTitle()}
          </Typography>
        </Box>
        <Divider style={{ borderStyle: 'dashed' }} sx={{ my: 2 }} />
        {isLoading ? (
          <Skeleton variant="rounded" width="100%" height={300} />
        ) : (
          <>
            {!visibleView && (
              <>
                {/* Wallet Section */}
                {isWalletAvailable && (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight="bold">{t('wallet_balance')}</Typography>
                      <Typography>{fCurrency(walletData?.amount)}</Typography>
                      <Switch
                        checked={isWallet}
                        onChange={toggleWallet}
                        color="success"
                        // disabled={amount > walletData?.amount}
                      />
                    </Box>
                    <Divider style={{ borderStyle: 'dashed' }} sx={{ my: 2 }} />
                  </>
                )}

                {/* Payment Types/Modes */}
                {paymentModesAvailable.map((mode) => (
                  <React.Fragment key={mode.id}>
                    <Box
                      key={mode.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      sx={{
                        cursor: isPaymentModeDisabled(mode.id) ? 'not-allowed' : 'pointer',
                        opacity: isPaymentModeDisabled(mode.id) ? 0.7 : 1,
                        pointerEvents: isPaymentModeDisabled(mode.id) ? 'none' : 'auto',
                        display:
                          mode.id === 'pad' &&
                          (user?.registeredAs === 'individual' || !user?.registeredAs)
                            ? 'none'
                            : 'flex',
                        borderRadius: 1,
                        px: 2,
                        '&:hover': {
                          backgroundColor: !isPaymentModeDisabled(mode.id)
                            ? 'action.hover'
                            : 'transparent',
                        },
                        // display: isPaymentModeDisabled(mode.id) ? 'none' : 'flex',
                      }}
                      onClick={() =>
                        !isPaymentModeDisabled(mode.id) && handlePaymentModeSelect(mode.id)
                      }
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        width="100%"
                      >
                        {selectedCard === mode.id ? (
                          <RadioButtonCheckedIcon color="primary" />
                        ) : (
                          <RadioButtonUncheckedIcon />
                        )}
                        <Box
                          sx={{
                            flexGrow: 999,
                          }}
                        >
                          <Typography ml={1}>{t(mode.i18Key)}</Typography>
                          {mode.id === 'credit' && (
                            <Typography ml={1} color="error" sx={{ opacity: 1 }}>
                              {creditDetails?.message}
                            </Typography>
                          )}
                        </Box>
                        {mode.id === 'credit' && (
                          <Typography>
                            {t('Remaining')} {fCurrency(creditDetails?.balanceCredit)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {mode?.id === 'pad' &&
                      padData?.paymentPendingOrders > 0 &&
                      padData?.success === 'false' && (
                        <Typography ml={4} px={2} color="error" sx={{ opacity: 1 }} fontSize={12}>
                          {t('Not_Paid_Previous_order')}
                        </Typography>
                      )}
                  </React.Fragment>
                ))}
                <Divider style={{ borderStyle: 'dashed' }} sx={{ my: 2 }} />

                {/* Saved Cards */}
                <Typography variant="subtitle1" mb={1}>
                  {t('Cards')}
                </Typography>
                {savedCards?.map((card) => (
                  <Box key={card.cardNumber + '' + card.first}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      onClick={() => handleCardSelect(card)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {selectedCard === card.cardNumber &&
                        card?.first === selectedCardDetails?.first ? (
                          <RadioButtonCheckedIcon color="primary" />
                        ) : (
                          <RadioButtonUncheckedIcon />
                        )}
                        <Typography ml={1}>**** {card.cardNumber}</Typography>
                      </Box>

                      <Icon icon={getCardTypeIcon(card.cardType)} width={32} />
                    </Box>
                    {card.cardNumber === selectedCardDetails?.cardNumber &&
                      card?.first === selectedCardDetails?.first && (
                        <Box mt={1} pl={4}>
                          <input
                            maxLength={4}
                            placeholder="CVV"
                            value={cvv || ''}
                            type="password"
                            onChange={(e) => {
                              setCvv(e.target.value);
                            }}
                            style={{
                              padding: '8px 12px',
                              width: '100px',
                              borderRadius: 4,
                              border: '1px solid #ccc',
                              outline: 'none',
                              fontSize: '14px',
                            }}
                          />
                        </Box>
                      )}
                  </Box>
                ))}

                {savedCards?.length === 0 && (
                  <EmptyContent
                    title={t('No_Cards')}
                    filled
                    sx={{ py: 2, width: '100%', my: 3, height: 200 }}
                  />
                )}

                {/* Pay Button */}
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 4 }}
                  disabled={!isWallet && !selectedCard}
                  onClick={handlePay}
                  loading={isLoading}
                >
                  {t('Pay')} {fCurrency(amount)}
                </Button>
              </>
            )}
            {visibleView === visibleViewsInPaymentModal.addCard && (
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '75vh',
                  p: 0,
                }}
              >
                <iframe
                  src={`${CONFIG.paymentsURL}getCardTemplateV2?userID=${userID}&email=${user?.email}&source=browser`}
                  title="Add Card"
                  allowFullScreen
                  width="100%"
                  height="100%"
                  style={{ display: 'block', border: 'none' }}
                />
              </Paper>
            )}

            {visibleView === visibleViewsInPaymentModal.stc && (
              <>
                <TextField
                  label={t('STC_Pay_Number')}
                  fullWidth
                  value={stcDetails?.number}
                  onChange={(e) => {
                    setStcDetails((prevState) => ({
                      ...prevState,
                      number: e.target.value,
                    }));
                  }}
                />
                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  color="primary"
                  size="medium"
                  fullWidth
                  loading={isLoading}
                  disabled={!isValidSTCPayNumber(stcDetails?.number)}
                  onClick={initSTCPayment}
                >
                  {t('Continue')}
                </Button>
              </>
            )}
            {visibleView === visibleViewsInPaymentModal.verifyStc && (
              <>
                <Typography sx={{ mb: 2 }}>
                  {t('Enter_the_verification_code_sent_to')} : {stcDetails.number}{' '}
                </Typography>
                <TextField
                  label={t('Verification_Code')}
                  fullWidth
                  value={stcDetails?.otp}
                  onChange={(e) => {
                    setStcDetails((prevState) => ({
                      ...prevState,
                      otp: e.target.value,
                    }));
                  }}
                />
                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  color="primary"
                  size="medium"
                  fullWidth
                  loading={isLoading}
                  onClick={verifySTCPayment}
                >
                  {t('Continue')}
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default React.memo(PaymentDrawer);
